import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import List from '../../../../../features/components/List'
import SkeletonListRow from '../../../../../features/components/List/Skeleton'
import RightActions from '../../../PartnershipRightsList/RightActions'
import DeliveredCol from '../../../PartnershipRightsList/RightsListTable/RightsListTableData/Cols/DeliveredCol'
import RightFilesTooltips from '../../../PartnershipRightsList/RightFilesTooltips'
import RightActionDropdown from '../../../PartnershipRightsList/RightActionDropdown'
import InternalLink from '../../../../../components/InternalLink'
import StatusUpdateDropdown from '../../../../../features/components/StatusUpdateDropdown'

import useIsAdvancedPartnership from '../../../../../features/hooks/useIsAdvancedPartnership'

import { replaceRouteParams } from '../../../../../helpers/url'
import { formatDateShort, formatDateSkipYear } from '../../../../../constants/dates'
import { formatCurrency, formatNumber } from '../../../../../helpers/numbers'
import { capitalizeFirstLetter, insertIf } from '../../../../../helpers/common'
import usePermissions from '../../../../../hooks/usePermissions'

import { openForm } from '../../../../../modules/actions/forms'
import { clearUpdateRight, deleteRight, updateRight } from '../../../../../modules/actions/rights'
import {
  deleteRightIdSelector,
  deleteRightIsLoadingSelector,
  updateRightErrorSelector,
  updateRightIdSelector,
  updateRightIsLoadingSelector,
  updateRightWasUpdatedSelector
} from '../../../../../modules/selectors/rights'
import { selectedPartnershipCurrency } from '../../../../../modules/selectors/partnerships'

import { ROUTE_PARAMS, ROUTES } from '../../../../../constants/routes'
import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  RIGHT_TYPE_DIGITAL,
  RIGHT_TYPE_ONSITE,
  RIGHT_TYPE_POSTS
} from '../../../../../forms/Multiplatform/Partnerships/RightForms/fields'
import { RIGHT_STATUS } from '../../../../../constants/rights'
import { RIGHT_EDIT, RIGHT_MANAGE_FILES } from '../../../../../constants/forms'
import { RIGHTS_WITH_DRAFT_STATUS } from '../../../../../constants/permissions'

import { rightStatusColors } from '../../helpers'

import useStyles from '../../../PartnershipRightsList/styles'

const RightsListTableData = ({ rightsList, tableColumnsSize }) => {
  const classes = useStyles()

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [isStatusUpdateRunning, setIsStatusUpdateRunning] = useState(false)

  const updateRightId = useSelector(updateRightIdSelector)
  const updateRightIsLoading = useSelector(updateRightIsLoadingSelector)
  const updateRightWasUpdated = useSelector(updateRightWasUpdatedSelector)
  const updateRightError = useSelector(updateRightErrorSelector)
  const deleteRightId = useSelector(deleteRightIdSelector)
  const deleteRightIsLoading = useSelector(deleteRightIsLoadingSelector)
  const { currency_symbol: currencySymbol } = useSelector(selectedPartnershipCurrency)

  const permissions = usePermissions()

  const rightsToShow = useMemo(() => {
    if (permissions.can('read', RIGHTS_WITH_DRAFT_STATUS)) {
      return rightsList
    } else {
      return rightsList.filter(right => right.status !== RIGHT_STATUS.DRAFT)
    }
  }, [rightsList, permissions])

  const isAdvancedPartnership = useIsAdvancedPartnership()

  const handleManageFilesClick = useCallback(
    (selectedRightId, preselectedFileType) => {
      // openManageFilesForm
      dispatch(
        openForm({
          id: selectedRightId,
          formName: RIGHT_MANAGE_FILES,
          ...(preselectedFileType && { otherParams: `preselectedFileType=${preselectedFileType}` })
        })
      )
    },
    [dispatch]
  )

  const openRightEditForm = useCallback(
    id => {
      dispatch(openForm({ id, formName: RIGHT_EDIT }))
    },
    [dispatch]
  )

  const handleDeleteRight = useCallback(
    id => {
      dispatch(deleteRight(id))
    },
    [dispatch]
  )

  const handleUpdateRightStatus = useCallback(
    (id, status) => {
      dispatch(
        updateRight(id, {
          status
        })
      )
    },
    [dispatch]
  )

  const rightsListColumns = useMemo(
    () => [
      {
        headTitle: 'Right',
        Component: right => {
          const {
            name,
            id,
            partnership_id: partnershipId,
            right_type: rightType,
            start_date: startDate,
            end_date: endDate
          } = right

          const hideDates = rightType === RIGHT_TYPE_DIGITAL && !isAdvancedPartnership

          const rightLink = replaceRouteParams(ROUTES.rightsSummary, {
            [ROUTE_PARAMS.partnership]: partnershipId,
            [ROUTE_PARAMS.rightId]: id
          })

          return (
            <>
              {rightType === RIGHT_TYPE_DIGITAL || rightType === RIGHT_TYPE_ONSITE || rightType === RIGHT_TYPE_POSTS ? (
                <InternalLink to={rightLink} className={classes.rightLink}>
                  {name}
                </InternalLink>
              ) : (
                name
              )}
              {!hideDates && (
                <div className={classes.subText}>
                  {startDate && formatDateSkipYear(startDate)}
                  {startDate && endDate && ' - '}
                  {endDate && formatDateShort(endDate)}
                </div>
              )}
            </>
          )
        },
        style: { minWidth: tableColumnsSize.name }
      },
      // ...insertIf(isShowAllPartners, {
      //   headTitle: 'Partnership',
      //   field: 'partnership',
      //   style: { minWidth: tableColumnsSize.partnership }
      // }),
      {
        headTitle: 'Allocation',
        Component: right => {
          const hideAllocation = isAdvancedPartnership && right['right_type'] === RIGHT_TYPE_POSTS

          if (hideAllocation) {
            return '-'
          }

          return right.allocation_type === DIGITAL_ALLOCATION_TYPE_BUDGET ? (
            <>
              <div>{formatCurrency(right['budget'], { min: 2, max: 2 }, { symbol: currencySymbol })}</div>
              <div className={classes.subText}>{t('Budget')}</div>
            </>
          ) : (
            <>
              <div>{formatNumber(right['allocation'])}</div>
              <div className={classes.subText}>
                {right['allocation_unit'] || capitalizeFirstLetter(right['allocation_type'])}
              </div>
            </>
          )
        },
        style: { minWidth: tableColumnsSize.allocation }
      },
      {
        headTitle: 'Delivered',
        Component: right => <DeliveredCol right={right} currencySymbol={currencySymbol} />,
        style: { minWidth: tableColumnsSize.delivered }
      },
      {
        headTitle: 'Status',
        Component: ({ id, status }) => {
          return (
            <StatusUpdateDropdown
              itemId={id}
              currentStatus={status}
              statuses={Object.values(RIGHT_STATUS)}
              statusColors={rightStatusColors}
              onStatusUpdate={handleUpdateRightStatus}
              setIsStatusUpdateRunning={setIsStatusUpdateRunning}
            />
          )
        },
        style: {
          minWidth: tableColumnsSize.status,
          textOverflow: 'unset'
        }
      },
      ...insertIf(!isAdvancedPartnership, {
        headTitle: 'Actions',
        Component: ({ creative_assets_required, creative_assets_date, logo_required, logo_date, action, id }) => {
          return (
            <RightActions
              creativeAssetsRequired={creative_assets_required}
              creativeAssetsDate={creative_assets_date}
              logoRequired={logo_required}
              logoDate={logo_date}
              action={action}
              openManageFilesClick={handleManageFilesClick}
              rightId={id}
            />
          )
        },
        className: classes.actions,
        style: { minWidth: tableColumnsSize.actions }
      }),
      {
        Component: ({ files, id, right_type: type }) => (
          <>
            {!isAdvancedPartnership && (
              <RightFilesTooltips
                files={files}
                fileIconClassName={classes.fileIcon}
                handleManageFilesClick={handleManageFilesClick}
                rightId={id}
              />
            )}
            <RightActionDropdown
              isAdvancedPartnership={isAdvancedPartnership}
              openManageFilesClick={handleManageFilesClick}
              handleDeleteRight={handleDeleteRight}
              openRightEditForm={openRightEditForm}
              type={type}
              id={id}
            />
          </>
        ),
        className: classes.fileCol,
        style: isAdvancedPartnership
          ? {
              minWidth: tableColumnsSize.file,
              maxWidth: tableColumnsSize.file
            }
          : {
              minWidth: tableColumnsSize.file,
              maxWidth: 180
            }
      }
    ],
    [
      t,
      classes,
      currencySymbol,
      tableColumnsSize,
      handleManageFilesClick,
      openRightEditForm,
      handleDeleteRight,
      handleUpdateRightStatus,
      isAdvancedPartnership
    ]
  )

  useEffect(() => {
    if (isStatusUpdateRunning && (updateRightWasUpdated || updateRightError)) {
      setIsStatusUpdateRunning(false)

      dispatch(clearUpdateRight())
    }
  }, [dispatch, isStatusUpdateRunning, updateRightError, updateRightWasUpdated])

  return (
    <List
      cols={rightsListColumns}
      listData={rightsToShow}
      itemUpdatingId={updateRightIsLoading ? updateRightId : ''}
      itemDeletingId={deleteRightIsLoading ? deleteRightId : ''}
      Skeleton={<SkeletonListRow cols={rightsListColumns} key={uuidv4()} />}
      isBigRow
    />
  )
}

RightsListTableData.propTypes = {
  rightsList: PropTypes.array.isRequired,
  tableColumnsSize: PropTypes.object.isRequired
}

export default RightsListTableData
